import React, { ReactNode } from "react";
import {
    FormDialog,
    FormDropdown,
    FormFields,
    FormTextField,
    FormValidationSummary,
    mapToDropdownOption,
    ValidationResult
} from "sprint-common-web";

import { CourseOfActionFormFields } from "../../../../common/course-of-action/CourseOfActionFormFields";
import { useCourseOfActionFormFieldsValues } from "../../../../common/course-of-action/useCourseOfActionFormFieldsValues";
import { CloudFileRejectionData } from "./CloudFileRejectionData";
import { CloudFileRejectionReasonFormInputs } from "./CloudFileRejectionReasonFormInputs";
import { useCloudFileRejectionReasonForm } from "./useCloudFileRejectionReasonForm";

type RejectCloudFileDialogFormProps = {
    title: string;
    description: string;
    width?: number | string;
    height?: number;
    validationResult?: ValidationResult;
    areActionsDisabled?: boolean;
    children?: ReactNode;
    onSubmit(data: CloudFileRejectionData): Promise<void>;
    onClose(): void;
};

export const RejectCloudFileDialogForm = ({
    title,
    description,
    width,
    height,
    children,
    validationResult,
    areActionsDisabled,
    onSubmit,
    onClose
}: RejectCloudFileDialogFormProps): JSX.Element => {
    const {
        control,
        errors,
        formState,
        handleSubmit,
        reset,
        areRejectionReasonsLoading,
        rejectionReasons
    } = useCloudFileRejectionReasonForm();
    const {
        courseOfActions,
        fixedBillings,
        areCourseOfActionsLoading,
        areFixedBillingsLoading
    } = useCourseOfActionFormFieldsValues();

    const onCancel = () => {
        onClose();
        reset();
    };

    const handleFormSubmit = async (inputs: CloudFileRejectionReasonFormInputs) => {
        const courseOfAction = courseOfActions.find((coa) => coa.id === inputs.courseOfActionId);
        const fixedBilling = fixedBillings.find((fb) => fb.id === inputs.fixedBillingId);
        const reason = rejectionReasons.find((r) => r.id === inputs.reasonId);

        if (!reason || !courseOfAction) return;

        await onSubmit({
            reason,
            courseOfAction,
            fixedBilling,
            details: inputs.details
        });
    };

    return (
        <FormDialog
            title={title}
            formState={formState}
            text={description}
            showCloseButton={false}
            submit={{
                onClick: handleSubmit(handleFormSubmit),
                disabled:
                    areRejectionReasonsLoading ||
                    areCourseOfActionsLoading ||
                    areFixedBillingsLoading ||
                    formState.isSubmitting ||
                    formState.isValidating ||
                    areActionsDisabled
            }}
            cancel={{
                onClick: onCancel
            }}
            width={width ?? 700}
            contentHeight={height ?? 400}
        >
            <FormFields>
                <FormDropdown
                    name="reasonId"
                    label="Reason"
                    options={rejectionReasons.map((reason) => mapToDropdownOption(reason))}
                    rules={{ required: "Select a reason" }}
                    control={control}
                    disabled={areRejectionReasonsLoading}
                    errors={errors}
                />
                <CourseOfActionFormFields
                    control={control}
                    errors={errors}
                    courseOfActions={courseOfActions}
                    fixedBillings={fixedBillings}
                />
                <FormTextField
                    name="details"
                    label="Details"
                    control={control}
                    errors={errors}
                    multiline={true}
                    height={100}
                />

                {!!validationResult && <FormValidationSummary validationResult={validationResult} />}
                {children}
            </FormFields>
        </FormDialog>
    );
};
